import type { Product as SwProduct, PropertyGroup } from '@hubblecommerce/hubble/platforms/shopware/api-client'
import type { HblProduct } from '@/utils/types'
import {
    hblMapMedia,
    hblMediaIncludes,
    hblMapManufacturer,
    hblManufacturerIncludes,
    hblMapPrice,
    hblPriceIncludes,
    hblMapProductMedia,
    hblProductMediaIncludes,
    hblMapVariantGroups,
    hblVariantGroupsIncludes,
    hblMapPropertyOptions
} from '#imports'
import { normalizeCustomField } from '@/utils/helper/normalizeCustomField'

export const hblProductIncludes = {
    'product': [
        'id',
        'translated',
        'name',
        'description',
        'productNumber',
        'seoUrls',
        'available',
        'stock',
        'calculatedCheapestPrice',
        'calculatedPrices',
        'calculatedPrice',
        'variantListingConfig',
        'parentId',
        'deliveryTime',
        'manufacturer',
        'metaTitle',
        'metaDescription',
        'configurator',
        'optionIds',
        'media',
        'cover',
        'minPurchase',
        'maxPurchase',
        'purchaseUnit',
        'unit',
        'weight',
        'packUnit',
        'childCount',
        'options',
        'customFields',
        'extensions',
        'parentSeoUrls',
        'sets',
        'variantCrossSelling',
        'variantListing',
        'lgEnrichedProductFlags',
        'lgVariantInfo',
    ],
    'lg_variant_info': [
        'purchasableVariantCount',
        'cheapestPurchasablePrice'
    ],
    'calculated_cheapest_price': [
        'unitPrice',
        'variantId',
        'listPrice',
        'calculatedTaxes',
        'referencePrice'
    ],
    'seo_url': [
        'pathInfo',
        'isCanonical',
        'seoPathInfo'
    ],
    'delivery_time': [
        'name',
        'translated',
        'customFields'
    ],
    ...hblProductMediaIncludes,
    ...hblMediaIncludes,
    ...hblVariantGroupsIncludes,
    ...hblPriceIncludes,
    ...hblManufacturerIncludes
}

export function hblMapProduct (swProduct: SwProduct, swProductConfigurator?: PropertyGroup[]): HblProduct {
    const seoUrls = swProduct?.extensions?.parentSeoUrls?.length ? swProduct.extensions.parentSeoUrls : swProduct?.seoUrls;

    const firstUrl = seoUrls?.[0];
    const fallbackUrl = `/detail/${swProduct?.id || ''}`;

    const pathInfo = firstUrl?.pathInfo || fallbackUrl;

    let url = firstUrl?.isCanonical ? firstUrl.seoPathInfo : firstUrl?.pathInfo;
    url = url ? (url.startsWith('/') ? url : `/${url}`) : fallbackUrl;

    let media = null
    if (swProduct.media != null) {
        media = hblMapProductMedia(swProduct.media)
    } else if (swProduct.cover?.media != null) {
        media = hblMapMedia(swProduct.cover.media)
    }

    let offerImage = null
    if (swProduct.translated?.customFields?.offer_image != null) {
        offerImage = hblMapMedia(swProduct.translated?.customFields?.offer_image)
    }

    let variants = null
    if (swProductConfigurator != null && swProductConfigurator.length > 0) {
        variants = hblMapVariantGroups(swProductConfigurator)
    }

    let defaultOptions = null
    if (swProduct.optionIds != null) {
        defaultOptions = swProduct.optionIds
    }

    let options = null
    if (swProduct.options != null) {
        options = hblMapPropertyOptions(swProduct.options)
    }

    let parentId = null
    if (swProduct.parentId != null) {
        parentId = swProduct.parentId
    }

    const _cheapest = swProduct?.calculatedCheapestPrice

    let _real = swProduct?.calculatedPrices != null && swProduct?.calculatedPrices?.length > 0
        ? swProduct?.calculatedPrices[0]
        : swProduct?.calculatedPrice

    const _displayFrom = swProduct?.calculatedPrices != null && swProduct?.calculatedPrices?.length > 1

    // @ts-ignore
    const _displayParent = swProduct?.extensions?.variantListing?.displayParent === true && swProduct?.parentId === null

    let _displayFromVariants = false

    if (_displayParent) {
        // @ts-ignore
        _displayFromVariants = _displayParent && _real?.unitPrice !== _cheapest?.unitPrice
        // @ts-ignore
        _real = _cheapest
    }

    // @ts-ignore
    const displayFromVariants = _cheapest?.unitPrice !== _real.unitPrice && _cheapest?.variantId !== swProduct?.id

    // @ts-ignore
    const _variantInfo = swProduct?.extensions?.lgVariantInfo

    const _purchasableVariantCount = typeof _variantInfo?.purchasableVariantCount === 'number'
        ? _variantInfo.purchasableVariantCount
        : null

    /**
     * The 'from' label is only meaningful with an actual choice of variants. Falls back to
     * childCount as long as the lgVariantInfo extension is not available.
     */
    const _hasVariantChoice = _purchasableVariantCount != null
        ? _purchasableVariantCount > 1
        : swProduct?.childCount != null && swProduct?.childCount > 0

    const displayFrom = _displayFrom || (_displayParent && _hasVariantChoice)

    const _price = () => {
        if (displayFrom && swProduct?.calculatedPrices != null && swProduct?.calculatedPrices?.length > 1) {
            const lowest = swProduct?.calculatedPrices?.reduce(
                (previous, current) => {
                    return current.unitPrice < previous.unitPrice ? current : previous
                }
            )
            return lowest || _cheapest
        }

        // calculatedCheapestPrice ignores orderability, so it may point at a sold out variant
        if (_displayParent && _purchasableVariantCount === 1 && _variantInfo?.cheapestPurchasablePrice != null) {
            return _variantInfo.cheapestPurchasablePrice
        }

        return _real
    }

    const price = hblMapPrice(_price())

    const variantsFrom = displayFromVariants && _hasVariantChoice

    const priceRange = displayFrom

    let cheapestPrice = null
    if (swProduct.calculatedCheapestPrice != null) {
        // @ts-ignore
        cheapestPrice = hblMapPrice(_cheapest)
    }

    const tierPrices: any = []
    if (swProduct.calculatedPrices != null && swProduct.calculatedPrices?.length > 0) {
        swProduct.calculatedPrices?.map((price) => {
            return tierPrices.push({
                ...hblMapPrice(price),
                qty: price.quantity
            })
        })
    }

    let purchaseUnit = null
    if(swProduct.purchaseUnit != null) {
        purchaseUnit = swProduct.purchaseUnit.toLocaleString('de-DE');
    }

    let productFlags = null
    if (swProduct.extensions?.lgEnrichedProductFlags != null) {
        productFlags = swProduct.extensions?.lgEnrichedProductFlags
    }

    return {
        // @ts-ignore
        id: swProduct.id,
        name: swProduct.translated?.name,
        description: swProduct.translated?.description,
        sku: swProduct.productNumber,
        // @ts-ignore
        pathInfo,
        url,
        // @ts-ignore
        active: swProduct.available,
        stock: swProduct.stock,
        // @ts-ignore
        min: swProduct.minPurchase,
        // @ts-ignore
        max: swProduct.maxPurchase,
        priceRange,
        price,
        variantsFrom,
        // @ts-ignore
        cheapestPrice,
        tierPrices,
        // @ts-ignore
        purchaseUnit,
        // @ts-ignore
        unit: swProduct.unit,
        weight: swProduct.weight,
        deliveryTime: swProduct.deliveryTime?.name,
        deliveryTimeCustomFields: swProduct.deliveryTime?.customFields,
        manufacturer: swProduct.manufacturer != null ? hblMapManufacturer(swProduct.manufacturer) : null,
        metaTitle: swProduct.translated?.metaTitle,
        metaDescription: swProduct.translated?.metaDescription,
        packUnit: swProduct?.packUnit,
        productFlags,
        ...(variants != null && { variants }),
        ...(defaultOptions != null && { defaultOptions }),
        ...(options != null && { options }),
        ...(parentId != null && { parentId }),
        ...(media != null && { media }),
        ...(swProduct.translated?.customFields != null && {
            customFields: {
                ...swProduct.translated.customFields,
                special_price: normalizeCustomField(swProduct.translated.customFields?.special_price),
                special_base_price: normalizeCustomField(swProduct.translated.customFields?.special_base_price),
                savings_text: normalizeCustomField(swProduct.translated.customFields?.savings_text)
            }
        }),
        ...(swProduct?.extensions?.sets?.[0] != null && {definedSet: swProduct?.extensions?.sets?.[0]?.defined }),
        ...(swProduct?.extensions?.variantCrossSelling != null && {variantCrossSelling: swProduct?.extensions?.variantCrossSelling })
    }
}
